/**
 * collectDroppedFiles — turn a folder selection or a drag-drop payload
 * into a flat ``File[]``.
 *
 * Folder upload flattens the whole tree: every document under the picked
 * folder (and all subfolders) is uploaded to the same library as a flat
 * document — its path inside the folder is kept as the document's folder tag
 * (see ``folderPaths``). Folder-sourced files are filtered to recognized document
 * types so OS junk (``.DS_Store``, hidden dotfiles, images, zips) never
 * lands in the library. An explicitly picked or dropped LOOSE file is
 * kept as-is — the writer chose it deliberately.
 */

import { recordRelativePath } from "./folderPaths";

/** Recognized source-document extensions (lower-case, no dot). */
export const DOCUMENT_EXTENSIONS: ReadonlySet<string> = new Set([
  "pdf",
  "docx",
  "doc",
  "txt",
  "md",
  "csv",
  "xlsx",
  "xls",
  "pptx",
  "ppt",
  "rtf",
]);

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return ""; // no ext, or leading-dot hidden file
  return name.slice(dot + 1).toLowerCase();
}

/**
 * True when a folder-sourced file should be uploaded: a recognized
 * document type, not a hidden dotfile, and non-empty. Loose files
 * picked/dropped directly bypass this filter.
 */
export function isUploadableDocument(file: File): boolean {
  if (!file.name || file.name.startsWith(".")) return false;
  if (file.size === 0) return false;
  return DOCUMENT_EXTENSIONS.has(extensionOf(file.name));
}

/** Filter a folder-sourced ``File[]`` (e.g. from a webkitdirectory input). */
export function filterFolderFiles(files: File[]): File[] {
  return files.filter(isUploadableDocument);
}

// --- Drag-drop directory traversal (webkitGetAsEntry / entries API) ---

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  /** "/Module 3/3.2.P/Spec.pdf" — the entry's path inside the drop. */
  fullPath?: string;
  file?: (onSuccess: (file: File) => void, onError?: (err: unknown) => void) => void;
  createReader?: () => {
    readEntries: (onSuccess: (entries: FileSystemEntryLike[]) => void, onError?: (err: unknown) => void) => void;
  };
}

function entryToFile(entry: FileSystemEntryLike): Promise<File | null> {
  return new Promise((resolve) => {
    if (!entry.file) {
      resolve(null);
      return;
    }
    entry.file(
      (file) => resolve(file),
      () => resolve(null),
    );
  });
}

function readAllDirectoryEntries(entry: FileSystemEntryLike): Promise<FileSystemEntryLike[]> {
  return new Promise((resolve) => {
    const reader = entry.createReader?.();
    if (!reader) {
      resolve([]);
      return;
    }
    const all: FileSystemEntryLike[] = [];
    const readBatch = () => {
      reader.readEntries(
        (batch) => {
          if (batch.length === 0) {
            resolve(all);
            return;
          }
          all.push(...batch);
          // readEntries only returns a slice per call — page until empty.
          readBatch();
        },
        () => resolve(all),
      );
    };
    readBatch();
  });
}

/**
 * Recurse an entry tree. ``fromDirectory`` tracks whether the file was
 * descended from a directory — those are filtered to document types;
 * top-level loose files are kept regardless.
 */
async function walkEntry(entry: FileSystemEntryLike, fromDirectory: boolean, out: File[]): Promise<void> {
  if (entry.isFile) {
    const file = await entryToFile(entry);
    if (!file) return;
    if (!fromDirectory || isUploadableDocument(file)) {
      // The library is flat; the folder path survives as the document's tag.
      if (fromDirectory && entry.fullPath) recordRelativePath(file, entry.fullPath);
      out.push(file);
    }
    return;
  }
  if (entry.isDirectory) {
    const children = await readAllDirectoryEntries(entry);
    for (const child of children) {
      await walkEntry(child, true, out);
    }
  }
}

/**
 * Flatten a drop payload into a ``File[]``. When the entries API is
 * available (Chromium/Edge — the add-in's runtime), dropped folders are
 * walked recursively and folder-sourced files filtered to documents.
 * Falls back to ``dataTransfer.files`` when the API is absent (dropped
 * folders then simply can't be expanded).
 */
export async function collectFilesFromDataTransfer(dt: DataTransfer): Promise<File[]> {
  const items = dt.items ? Array.from(dt.items) : [];
  const supportsEntries = items.length > 0 && typeof items[0].webkitGetAsEntry === "function";

  if (!supportsEntries) {
    return Array.from(dt.files ?? []);
  }

  const entries = items
    .filter((item) => item.kind === "file")
    .map((item) => item.webkitGetAsEntry() as FileSystemEntryLike | null)
    .filter((e): e is FileSystemEntryLike => e !== null);

  const out: File[] = [];
  for (const entry of entries) {
    await walkEntry(entry, false, out);
  }
  return out;
}
